import { NativeModules, Platform, Settings } from 'react-native';
import { createSecureAuthStorage } from './secureAuthStorage';
import { createNativeCredentialStorage } from './nativeCredentialStorage';
import { isLocalEndpoint } from '../lib/localEndpoint';
import { createWindowsHtmlPreviewStorage } from './windowsHtmlPreviewStorage';

// No-op storage for SSR (Node.js) where window/localStorage are unavailable.
// Zustand persist will harmlessly skip get/set during server-side rendering.
export const noopStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

// AsyncStorage's native module on macOS doesn't persist writes to disk.
// Use NSUserDefaults for preferences only. Auth is routed to Keychain below.
const macOSStorage = Platform.OS === 'macos' ? {
  getItem: (key) => {
    const val = Settings.get(key);
    return Promise.resolve(val != null ? val : null);
  },
  setItem: (key, value) => {
    Settings.set({ [key]: value });
    return Promise.resolve();
  },
  removeItem: (key) => {
    Settings.set({ [key]: null });
    return Promise.resolve();
  },
} : null;

let nativeStorage;
let windowsPreviewStorage;

function localMacDevelopment() {
  if (Platform.OS !== 'macos' || typeof __DEV__ === 'undefined' || !__DEV__) return false;
  // Shares the classifier with the Release endpoint safety net in
  // constants/config.js so LAN and expanded-IPv6 loopback QA also isolate.
  return isLocalEndpoint(process.env.EXPO_PUBLIC_API_URL || '');
}

/** Uses secure native auth storage, retaining AsyncStorage on older mobile binaries. */
function getNativeStorage() {
  if (!nativeStorage) {
    const localDevelopment = localMacDevelopment();
    // Debug and installed editions share a bundle identity. Local QA must
    // neither migrate production credentials nor set its sign-out fence.
    const ordinary = localDevelopment ? {
      getItem: key => macOSStorage.getItem('local-qa.' + key),
      setItem: (key, value) => macOSStorage.setItem('local-qa.' + key, value),
      removeItem: key => macOSStorage.removeItem('local-qa.' + key),
    } : Platform.OS === 'macos' ? macOSStorage : (() => {
      const module = require('@react-native-async-storage/async-storage');
      return module.default || module;
    })();
    nativeStorage = createSecureAuthStorage(ordinary, createNativeCredentialStorage({ localDevelopment }));
  }
  return nativeStorage;
}

export function getStorage() {
  if (Platform.OS === 'web') return typeof window !== 'undefined' ? localStorage : noopStorage;
  if (Platform.OS === 'windows') {
    // Preview-only calls must not initialize mobile AsyncStorage/SecureStore:
    // the dedicated Windows application owns its session through WindowsSession.
    if (!windowsPreviewStorage) windowsPreviewStorage = createWindowsHtmlPreviewStorage(NativeModules.WindowsHtmlPreviewStorage, {
      getItem: key => getNativeStorage().getItem(key),
      setItem: (key, value) => getNativeStorage().setItem(key, value),
      removeItem: key => getNativeStorage().removeItem(key),
    });
    return windowsPreviewStorage;
  }
  return getNativeStorage();
}
